import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  useApproveHandoverProtocol,
  useHandoverProtocols,
  useRejectHandoverProtocol,
} from "../hooks/useAssetRegistry";
import { extractError } from "../utils";
import { ProtocolCard } from "./ProtocolCard";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = "pending" | "approved" | "rejected" | "all";

const TABS: { value: Tab; label: string }[] = [
  { value: "pending", label: "Do akceptacji" },
  { value: "approved", label: "Zaakceptowane" },
  { value: "rejected", label: "Odrzucone" },
  { value: "all", label: "Wszystkie" },
];

export function ManagerProtocolsModal({ open, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("pending");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [error, setError] = useState("");

  const params = tab === "all" ? undefined : { status: tab };
  const { data: protocols = [], isLoading } = useHandoverProtocols(params, open);
  const approve = useApproveHandoverProtocol();
  const reject = useRejectHandoverProtocol();

  function doApprove(id: string) {
    setError("");
    approve.mutate({ id }, { onError: (e) => setError(extractError(e)) });
  }

  function doReject(id: string) {
    if (!rejectComment.trim()) {
      setError("Podaj powód odrzucenia.");
      return;
    }
    reject.mutate(
      { id, comment: rejectComment.trim() },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectComment("");
        },
        onError: (e) => setError(extractError(e)),
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Protokoły przekazania" size="lg">
      <div className="flex gap-1 mb-4 border-b border-[var(--color-border)]">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => {
              setTab(t.value);
              setRejectingId(null);
              setError("");
            }}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider -mb-px border-b-2 ${
              tab === t.value
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

      {isLoading ? (
        <div className="py-8 flex justify-center">
          <Spinner />
        </div>
      ) : protocols.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)] py-6 text-center">Brak wniosków.</p>
      ) : (
        <div className="space-y-3">
          {protocols.map((p) => (
            <ProtocolCard key={p.id} protocol={p} showEmployee>
              {p.status === "pending" && (
                <div className="pt-2 border-t border-[var(--color-border)] mt-2">
                  {rejectingId === p.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                        rows={2}
                        placeholder="Powód odrzucenia…"
                        className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRejectingId(null);
                            setRejectComment("");
                          }}
                        >
                          Anuluj
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => doReject(p.id)}
                          loading={reject.isPending}
                        >
                          Potwierdź odrzucenie
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setRejectingId(p.id);
                          setRejectComment("");
                          setError("");
                        }}
                      >
                        Odrzuć
                      </Button>
                      <Button size="sm" onClick={() => doApprove(p.id)} loading={approve.isPending}>
                        Zatwierdź
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </ProtocolCard>
          ))}
        </div>
      )}
    </Modal>
  );
}
